import React, { useCallback, useEffect, useMemo, useState } from "react";
import { TEAM_EXPORT_TAB_FIELDS } from "../utils/exportTabFields";
import { Link, useNavigate } from "react-router-dom";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  TableCell,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import { Import } from "iconsax-react";

import useAuthController from "../hooks/useAuthController";
import Breadcrumb from "../components/Breadcrumb";
import StatsSlider from "../components/StatsSlider";
import GenericDataTable from "../components/GenericDataTable";
import TopListHeaderBar from "../components/TopListHeaderBar";
import TabNavigationBar from "../components/TabNavigationBar";
import BulkActionsBar from "../components/BulkActionsBar";
import CsvImportDialog from "../components/CsvImportDialog";
import useCsvImport from "../hooks/useCsvImport";
import { MatchActionIcon } from "../components/ActionButtonIcons";
import ListTableTabsTemplate from "../components/layouts/ListTableTabsTemplate";
import {
  createTeamMemberIfMissing,
  fetchRecruiterUsers,
  fetchTeamMembers,
  fetchTeamRoster,
} from "../utils/teamMembers";
import { formatRelativeTimeShort } from "../utils/relativeTime";
import TeamMemberAddIcon from "../components/icons/TeamMemberAddIcon";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const PURPLE = "#9C4FFF";
const TEXT = "#344054";
const MUTED = "#667085";
const BORDER = "#EAECF0";
const EMPTY_VALUE = "-";

const STATUS_OPTIONS = [
  "Active",
  "Available",
  "Interviewing",
  "New",
  "Contacted",
  "Not Available",
];

const TEAM_TABS = [
  { id: "overview", label: "Overview" },
  { id: "members", label: "Team Members" },
  { id: "available", label: "Available" },
];

const STATUS_STYLES = {
  active: { color: "#12B76A", bg: "#ECFDF3", border: "#ABEFC6" },
  available: { color: "#12B76A", bg: "#ECFDF3", border: "#ABEFC6" },
  interviewing: { color: "#F79009", bg: "#FFFAEB", border: "#FEDF89" },
  new: { color: "#1570EF", bg: "#EFF8FF", border: "#B2DDFF" },
  contacted: { color: "#F04438", bg: "#FEF3F2", border: "#FECDCA" },
  "not available": { color: "#F04438", bg: "#FEF3F2", border: "#FECDCA" },
  default: { color: "#475467", bg: "#F2F4F7", border: "#D0D5DD" },
};

const ENGAGEMENT_FALLBACKS = [
  "RPO",
  "Contingency",
  "Retained",
  "Contracting",
  "Flat Fee",
  "EOR",
];

const AVATAR_FALLBACKS = ["#F5E8FF", "#EAF2FF", "#FFECCF", "#EAFBF0", "#FFF0F5", "#E6F7F8"];

function cleanText(value) {
  return String(value ?? "").trim();
}

function pickFirst(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
      continue;
    }
    if (Array.isArray(value)) {
      if (value.length) return value;
      continue;
    }
    return value;
  }
  return "";
}

function normalizeApiBase(base) {
  return String(base || "").trim().replace(/\/$/, "");
}

function buildApiCandidates(apiBase, path) {
  const base = normalizeApiBase(apiBase);
  const rootBase = base.replace(/\/api$/i, "");
  return Array.from(
    new Set(
      [
        base ? `${base}${path.startsWith("/") ? path : `/${path}`}` : "",
        rootBase ? `${rootBase}${path.startsWith("/") ? path : `/${path}`}` : "",
        path.startsWith("/") ? path : `/${path}`,
      ].filter(Boolean)
    )
  );
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    ""
  );
}

function authHeaders(token = getToken()) {
  return token
    ? {
        Authorization: `Bearer ${token}`,
        "x-access-token": token,
      }
    : {};
}

function hashString(input = "") {
  const value = String(input || "");
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(index);
    hash |= 0;
  }
  return Math.abs(hash);
}

function pickSeed(list, seed, fallback = "") {
  if (!Array.isArray(list) || !list.length) return fallback;
  return list[Math.abs(Number(seed) || 0) % list.length] || fallback;
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function toTitleCase(value) {
  return cleanText(value)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function toNumberOrNull(value) {
  if (value === "" || value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getInitials(name) {
  const parts = cleanText(name).split(/\s+/).filter(Boolean);
  if (!parts.length) return "TM";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}

function normalizeStatusKey(value) {
  return cleanText(value).toLowerCase();
}

function normalizeTeamStatus(value) {
  const raw = cleanText(value).toLowerCase();
  if (raw) {
    if (raw.includes("not") || raw.includes("unavailable") || raw.includes("inactive")) {
      return "Not Available";
    }
    if (raw.includes("interview")) return "Interviewing";
    if (raw.includes("contact")) return "Contacted";
    if (raw.includes("new")) return "New";
    if (raw.includes("available") || raw.includes("open")) return "Available";
    if (raw.includes("active") || raw.includes("current")) return "Active";
  }

  return "Unknown";
}

function escapeCsvValue(value) {
  const raw = String(value ?? "");
  if (!/[",\n]/.test(raw)) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
}

function downloadCsv(rows = []) {
  const headers = [
    "Team Member",
    "Role",
    "Email",
    "Status",
    "Industry Expertise",
    "Location",
    "Engagement Type",
    "Assignments",
    "Placements",
    "Last Activity",
    "Owner",
  ];

  const lines = [
    headers.join(","),
    ...(rows || []).map((row) =>
      [
        row.name,
        row.role,
        row.email,
        row.status,
        row.industryExpertise,
        row.location,
        row.engagementType,
        row.assignments,
        row.placements,
        row.lastActivity,
        row.owner,
      ]
        .map(escapeCsvValue)
        .join(",")
    ),
  ];

  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `team-members-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function normalizeImportedRecord(record = {}) {
  const normalized = Object.fromEntries(
    Object.entries(record || {}).map(([key, value]) => [
      String(key).trim().toLowerCase().replace(/[ _-]+/g, "_"),
      value,
    ])
  );
  return {
    id: cleanText(normalized.id || normalized.user_id || normalized.team_member_id),
    name: cleanText(
      normalized.name || normalized.team_member || normalized.full_name
    ),
    email: cleanText(normalized.email),
    title: cleanText(normalized.title || normalized.role || normalized.job_title),
    role: cleanText(normalized.role),
    status: cleanText(normalized.status),
    location: cleanText(normalized.location),
    industry: cleanText(
      normalized.industry || normalized.industry_expertise
    ),
    engagement_type: cleanText(
      normalized.engagement_type || normalized.engagement
    ),
    assignments: cleanText(normalized.assignments || normalized.active_assignments),
    placements: cleanText(normalized.placements),
    owner: cleanText(normalized.owner || normalized.manager),
  };
}

function getTeamRowKey(row = {}) {
  const id = cleanText(row.id);
  if (id) return `id:${id}`;
  return `name:${cleanText(row.name).toLowerCase()}`;
}

function mergeNormalizedRows(existingRows = [], incomingRows = []) {
  const map = new Map();

  (existingRows || []).forEach((row) => {
    if (!row) return;
    map.set(getTeamRowKey(row), row);
  });

  (incomingRows || []).forEach((row) => {
    if (!row) return;
    map.set(getTeamRowKey(row), row);
  });

  return Array.from(map.values()).sort((left, right) => left.name.localeCompare(right.name));
}

async function fetchRecruiterProfileRows({ apiBase }) {
  const urls = buildApiCandidates(apiBase, "/api/user_info");
  let lastError = null;

  for (const url of urls) {
    try {
      const response = await fetch(url, {
        headers: {
          ...authHeaders(),
        },
      });

      if (!response.ok) {
        lastError = new Error(`Failed to load team profiles (${response.status})`);
        continue;
      }

      const payload = await response.json().catch(() => []);
      return Array.isArray(payload) ? payload : [];
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) throw lastError;
  return [];
}

function buildNormalizedTeamRow(source, { index = 0 } = {}) {
  const name = cleanText(source?.name || source?.full_name || source?.team_member || source?.teamMember);
  if (!name) return null;

  const uniqueBase = pickFirst(
    source?.id,
    source?.user_id,
    source?.email,
    source?.team_member_id,
    name,
    index
  );
  const role = cleanText(source?.role);
  const normalizedStatus = normalizeTeamStatus(
    pickFirst(source?.status, source?.availability, source?.team_status)
  );

  const explicitAssignments = toNumberOrNull(
    pickFirst(source?.assignments, source?.assignments_count, source?.active_assignments)
  );
  const explicitPlacements = toNumberOrNull(
    pickFirst(source?.placements, source?.placements_count)
  );

  const assignments =
    explicitAssignments != null
      ? Math.max(0, explicitAssignments)
      : 0;

  const placements =
    explicitPlacements != null
      ? Math.max(0, explicitPlacements)
      : 0;

  const title = cleanText(source?.title || source?.job_title);
  const fallbackTitle = role ? toTitleCase(role) : "";

  const location = cleanText(source?.location);
  const industryExpertise =
    cleanText(source?.industry || source?.industry_expertise || source?.department);
  const engagementType =
    cleanText(source?.engagement_type || source?.engagementType);
  const owner = cleanText(source?.owner || source?.manager);
  const lastActivity = cleanText(source?.last_activity || source?.lastActivity);

  return {
    id: cleanText(source?.id || source?.user_id) || `team-${uniqueBase}`,
    name,
    title: title || fallbackTitle,
    email: cleanText(source?.email),
    role: role || "team member",
    avatarUrl: cleanText(source?.avatar_url || source?.profile_image || source?.avatar),
    status: normalizedStatus,
    industryExpertise,
    location,
    engagementType,
    assignments,
    placements,
    lastActivity,
    lastActivityDescription: cleanText(
      source?.last_activity_description || source?.lastActivityDescription
    ),
    candidatesCount: Math.max(0, toNumberOrNull(source?.candidates_count) || 0),
    clientsCount: Math.max(0, toNumberOrNull(source?.clients_count) || 0),
    activitiesCount: Math.max(0, toNumberOrNull(source?.activities_count) || 0),
    recruitingMetrics: source?.recruiting_metrics || source?.recruitingMetrics || {},
    salesMetrics: source?.sales_metrics || source?.salesMetrics || {},
    owner,
    raw: source,
  };
}

function StatusChip({ value }) {
  const key = normalizeStatusKey(value);
  const style = STATUS_STYLES[key] || STATUS_STYLES.default;

  return (
    <Chip
      label={value || EMPTY_VALUE}
      size="small"
      sx={{
        height: 24,
        fontWeight: 600,
        fontSize: 11,
        borderRadius: "999px",
        color: style.color,
        backgroundColor: style.bg,
        border: `1px solid ${style.border}`,
        "& .MuiChip-label": {
          px: 1.2,
        },
      }}
    />
  );
}

function TeamMemberCell({ row, onOpen }) {
  const fallbackBg = pickSeed(AVATAR_FALLBACKS, hashString(row.name), AVATAR_FALLBACKS[0]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen?.(row);
    }
  };

  return (
    <Stack
      direction="row"
      spacing={1.2}
      alignItems="center"
      role="link"
      tabIndex={0}
      onClick={() => onOpen?.(row)}
      onKeyDown={handleKeyDown}
      sx={{
        minWidth: 0,
        cursor: "pointer",
        borderRadius: 1,
        outline: "none",
        "&:focus-visible": { boxShadow: `0 0 0 2px ${PURPLE}` },
        "&:hover .team-member-name": { color: PURPLE },
      }}
    >
      <Avatar
        src={row.avatarUrl || undefined}
        alt={row.name}
        sx={{
          width: 30,
          height: 30,
          fontSize: 11,
          fontWeight: 700,
          color: PURPLE,
          backgroundColor: fallbackBg,
        }}
      >
        {getInitials(row.name)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          className="team-member-name"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            color: TEXT,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            color: MUTED,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.role || EMPTY_VALUE}
        </Typography>
      </Box>
    </Stack>
  );
}

function AssignmentsCell({ row }) {
  if (!Number(row.assignments)) {
    return (
      <Typography sx={{ fontSize: 14, color: MUTED }}>
        0
      </Typography>
    );
  }

  return (
    <Typography
      sx={{
        fontSize: 14,
        fontWeight: 600,
        color: PURPLE,
        whiteSpace: "nowrap",
      }}
    >
      {row.assignments} Active
    </Typography>
  );
}

export function TeamOverviewCard({ member, onOpen, onAction }) {
  const avatarBackground = pickSeed(AVATAR_FALLBACKS, hashString(member.name), AVATAR_FALLBACKS[0]);
  const [activeDetailTab, setActiveDetailTab] = useState("recruiting");
  const recruiting = member.recruitingMetrics || {};
  const sales = member.salesMetrics || {};
  const recruitingMetrics = [
    ["Sourced", Number(recruiting.sourced || 0)],
    ["Screened", Number(recruiting.screened || 0)],
    ["Interview", Number(recruiting.interview || 0)],
    ["Offer", Number(recruiting.offer || 0)],
    ["Hired", Number(recruiting.hired || 0)],
  ];
  const salesMetrics = [
    ["Leads", Number(sales.leads || 0)],
    ["Clients", Number(sales.clients || 0)],
    ["Meetings", Number(sales.meetings || 0)],
    ["Proposals", Number(sales.proposals || 0)],
    ["Won", Number(sales.won || 0)],
  ];
  const overviewMetrics = [
    ["Assignments", Number(member.assignments || 0)],
    ["Placements", Number(member.placements || 0)],
    ["Expertise", member.industryExpertise || EMPTY_VALUE],
  ];
  const memberSummary = [
    ["Jobs", Number(member.assignments || 0)],
    ["Candidates", Number(member.candidatesCount || 0)],
    ["Clients", Number(member.clientsCount || 0)],
    ["Activities", Number(member.activitiesCount || 0)],
  ];
  const metrics = activeDetailTab === "recruiting" ? recruitingMetrics : activeDetailTab === "sales" ? salesMetrics : overviewMetrics;
  const activityDescription = member.lastActivityDescription || "No recent activity.";
  const memberPath = `/team/${encodeURIComponent(String(member.id || member.user_id))}`;
  const destination = (label) => {
    const tabs = { Jobs: "jobs", Assignments: "jobs", Candidates: "candidates", Clients: "clients", Activities: "activities", Leads: "leads", Meetings: "activities", Proposals: "activities", Won: "clients", Expertise: "details" };
    if (tabs[label]) return `${memberPath}?tab=${tabs[label]}`;
    const stage = label === "Placements" ? "hired" : label.toLowerCase();
    return `/candidates?view=pipeline&stage=${encodeURIComponent(stage)}`;
  };
  const linkSx = { display: "block", color: "inherit", textDecoration: "none", borderRadius: 1, "&:hover": { bgcolor: "#F4EBFF" }, "&:focus-visible": { outline: `2px solid ${PURPLE}`, outlineOffset: 2 } };

  return <Card sx={{ p: 2.25, border: "1px solid #D6DCE5", borderRadius: 2, boxShadow: "none", minWidth: 0 }}>
    <Stack direction="row" alignItems="center" spacing={1.25}>
      <Avatar src={member.avatarUrl || undefined} sx={{ width: 48, height: 48, bgcolor: avatarBackground, color: PURPLE, fontSize: 16 }}>{getInitials(member.name)}</Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="button" onClick={() => onOpen(member)} sx={{ p: 0, border: 0, background: "transparent", cursor: "pointer", color: "#111827", fontSize: 16, lineHeight: 1.12, fontWeight: 800, textAlign: "left", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", "&:hover": { color: PURPLE } }}>{member.name}</Typography>
        <Typography sx={{ mt: 0.3, fontSize: 13, color: "#6B778F" }} noWrap>{member.role || "Team Member"}</Typography>
      </Box>
      <Stack direction="row" spacing={0.65} alignItems="center" sx={{ color: "#6B778F", flex: "0 0 auto" }}><Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: member.status === "Not Available" ? "#F04438" : "#4BA6F7" }} /><Typography sx={{ fontSize: 11, fontWeight: 500, whiteSpace: "nowrap" }}>{member.status || "Available"}</Typography></Stack>
    </Stack>

    <Box sx={{ borderTop: "1px solid #E8EBF0", mt: 2, pt: 1.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", textAlign: "center", p: 1.1, borderRadius: 1.5, bgcolor: "#FAFAFB" }}>
        {memberSummary.map(([label, value]) => <Box key={label} component={Link} to={destination(label)} aria-label={`${member.name}: ${label} (${value})`} sx={linkSx}><Typography sx={{ fontSize: 17, fontWeight: 800, color: "#111827" }}>{formatNumber(value)}</Typography><Typography sx={{ fontSize: 9.5, color: "#6B778F" }}>{label}</Typography></Box>)}
      </Box>
      <Box sx={{ borderTop: "1px solid #E8EBF0", mt: 1.5, pt: 1.25 }}>
      <Stack direction="row" spacing={2} role="tablist" aria-label={`${member.name} metrics`}>
        {[["overview", "Overview"], ["recruiting", "Recruiting"], ["sales", "Sales"]].map(([value, label]) => {
          const selected = activeDetailTab === value;
          return <Button key={value} role="tab" aria-selected={selected} onClick={() => setActiveDetailTab(value)} variant="text" disableRipple sx={{ minWidth: 0, p: 0, pb: 0.8, border: 0, borderBottom: selected ? "3px solid #3858F5" : "3px solid transparent", borderRadius: 0, color: selected ? "#3858F5" : "#8256FF", fontSize: 13, fontWeight: selected ? 700 : 400, lineHeight: 1.2, textTransform: "none", "&:hover": { border: 0, borderBottom: selected ? "3px solid #3858F5" : "3px solid transparent", backgroundColor: "transparent", color: "#3858F5" } }}>{label}</Button>;
        })}
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))`, gap: 0.5, py: 1.75 }}>
        {metrics.map(([label, value]) => <Box key={label} component={Link} to={destination(label)} aria-label={`${member.name}: ${label} (${value})`} sx={{ ...linkSx, minWidth: 0, textAlign: "center" }}><Typography sx={{ fontSize: 16, fontWeight: 800, color: label === "Hired" || label === "Won" ? "#4DBB78" : "#111827" }} noWrap>{typeof value === "number" ? formatNumber(value) : value}</Typography><Typography sx={{ mt: 0.2, fontSize: 10.5, color: "#6B778F", lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Typography></Box>)}
      </Box>

      <Box sx={{ pt: 1.35, borderTop: "1px solid #E8EBF0" }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="baseline">
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#98A2B3", letterSpacing: "0.04em", textTransform: "uppercase" }}>Recent Activity</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#98A2B3" }} noWrap>{formatRelativeTimeShort(member.lastActivity, { emptyValue: "Recently" })}</Typography>
        </Stack>
        <Typography component={Link} to={destination("Activities")} sx={{ ...linkSx, mt: 0.45, fontSize: 13, color: "#111827", lineHeight: 1.35 }}>{activityDescription}</Typography>
      </Box>
      </Box>
    </Box>

    <Box sx={{ position: "relative", mt: 1.65, minHeight: 32, width: "100%", pr: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.25}>{[["Assign", "assign", PersonAddAlt1OutlinedIcon], ["Calendar", "calendar", CalendarMonthOutlinedIcon], ["Agent", "agent", StorageOutlinedIcon]].map(([label, action, Icon]) => <Button key={action} onClick={() => onAction(action, member)} variant="text" startIcon={<Icon />} sx={{ minWidth: 0, px: 0, color: "#5E626A", textTransform: "none", fontWeight: 600, fontSize: 12, "& .MuiButton-startIcon svg": { fontSize: 17 } }}>{label}</Button>)}</Stack>
      <IconButton aria-label={`More actions for ${member.name}`} onClick={() => onAction("more", member)} size="small" sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)", color: "#5E626A" }}><MoreVertIcon fontSize="small" /></IconButton>
    </Box>
  </Card>;
}

function TeamOverviewDashboard({ members, counts, onOpenMember, onMemberAction }) {
  const activities = members.slice(0, 5);
  const coverage = [...members].filter((member) => Number(member.assignments) > 0).sort((a, b) => Number(b.assignments) - Number(a.assignments)).slice(0, 5);
  const pipeline = [
    ["Screened", members.reduce((total, member) => total + Number(member.recruitingMetrics?.screened || 0), 0)],
    ["Presented", members.reduce((total, member) => total + Number(member.recruitingMetrics?.presented || 0), 0)],
    ["Interview", members.reduce((total, member) => total + Number(member.recruitingMetrics?.interview || 0), 0)],
    ["Offer", members.reduce((total, member) => total + Number(member.recruitingMetrics?.offer || 0), 0)],
    ["Hired", members.reduce((total, member) => total + Number(member.recruitingMetrics?.hired || 0), 0)],
  ];
  return <Box sx={{ p: { xs: 2, md: 3 } }}>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>{members.map((member) => <TeamOverviewCard key={member.id} member={member} onOpen={onOpenMember} onAction={onMemberAction} />)}</Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.15fr 1.15fr 1fr" }, gap: 2, mt: 3 }}>
      <Card sx={{ p: 2.25, border: `1px solid ${BORDER}`, borderRadius: 2, boxShadow: "none" }}><Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT }}>Recent Team Activities</Typography><Stack spacing={1.25} sx={{ mt: 1.75 }}>{activities.length ? activities.map((member) => <Stack key={member.id} direction="row" spacing={1} alignItems="flex-start"><Box sx={{ mt: 0.75, width: 6, height: 6, borderRadius: "50%", bgcolor: PURPLE, flex: "0 0 auto" }} /><Typography sx={{ flex: 1, fontSize: 13, color: TEXT, lineHeight: 1.45 }}><Box component="span" sx={{ fontWeight: 700 }}>{member.name}</Box>{member.lastActivityDescription ? ` ${member.lastActivityDescription}` : " has no recorded activity details."}</Typography><Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>{formatRelativeTimeShort(member.lastActivity, { emptyValue: "-" })}</Typography></Stack>) : <Typography sx={{ fontSize: 13, color: MUTED }}>No recent team activity.</Typography>}</Stack></Card>
      <Card sx={{ p: 2.25, border: `1px solid ${BORDER}`, borderRadius: 2, boxShadow: "none" }}><Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT }}>Team Pipeline Overview</Typography><Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 0.75, mt: 2 }}>{pipeline.map(([label, value]) => <Box key={label} sx={{ minWidth: 0, textAlign: "center" }}><Typography sx={{ fontSize: 18, fontWeight: 700, color: label === "Hired" ? "#12B76A" : TEXT }}>{formatNumber(value)}</Typography><Typography sx={{ fontSize: 11, color: MUTED }}>{label}</Typography></Box>)}</Box><Box sx={{ mt: 2, pt: 1.5, borderTop: `1px solid ${BORDER}` }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT }}>Top Performers</Typography>{coverage.slice(0, 3).map((member, index) => <Stack key={member.id} direction="row" justifyContent="space-between" sx={{ mt: 1 }}><Typography sx={{ fontSize: 13, color: TEXT }}>{index + 1}. {member.name}</Typography><Typography sx={{ fontSize: 12, color: MUTED }}>{member.placements} placements</Typography></Stack>)}</Box></Card>
      <Card sx={{ p: 2.25, border: `1px solid ${BORDER}`, borderRadius: 2, boxShadow: "none" }}><Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT }}>Open Roles Coverage</Typography><Stack spacing={1.35} sx={{ mt: 1.75 }}>{coverage.length ? coverage.map((member) => <Stack key={member.id} direction="row" spacing={1} alignItems="center"><Avatar src={member.avatarUrl || undefined} sx={{ width: 26, height: 26, fontSize: 10, bgcolor: "#F4EBFF", color: PURPLE }}>{getInitials(member.name)}</Avatar><Box sx={{ minWidth: 0, flex: 1 }}><Typography sx={{ fontSize: 13, fontWeight: 600, color: TEXT }} noWrap>{member.title || "Open role coverage"}</Typography><Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>{member.assignments} active assignments</Typography></Box><Typography sx={{ fontSize: 12, color: PURPLE, fontWeight: 600 }} noWrap>{member.name}</Typography></Stack>) : <Typography sx={{ fontSize: 13, color: MUTED }}>No active role coverage.</Typography>}</Stack></Card>
    </Box>
  </Box>;
}

function TeamContent({ user }) {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("overview");
  const [activeStatId, setActiveStatId] = useState("members");
  const [teamFilters, setTeamFilters] = useState({ role: "", status: "", location: "" });
  const [selectedIds, setSelectedIds] = useState([]);
  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuMember, setRowMenuMember] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [statusFeedback, setStatusFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [csvImportOpen, setCsvImportOpen] = useCsvImport();
  const [addSaving, setAddSaving] = useState(false);
  const [addForm, setAddForm] = useState({
    name: "",
    email: "",
    title: "",
    location: "",
    status: "New",
    industry: "",
    engagementType: "RPO",
  });

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      severity,
      message,
    });
  }, []);

  useEffect(() => {
    let active = true;

    const loadTeamPage = async () => {
      setLoading(true);
      setLoadError("");

      try {
        const [rosterMembers, recruiterUsers, teamMembers, profileRows] = await Promise.all([
          fetchTeamRoster({ apiBase: API_BASE }).catch(() => []),
          fetchRecruiterUsers({ apiBase: API_BASE }).catch(() => []),
          fetchTeamMembers({ apiBase: API_BASE }).catch(() => []),
          fetchRecruiterProfileRows({ apiBase: API_BASE }).catch(() => []),
        ]);

        if (!active) return;

        const recruiterProfiles = (Array.isArray(profileRows) ? profileRows : []).filter((record) => {
          const role = cleanText(record?.role).toLowerCase();
          return ["recruiter", "admin", "administrator", "system_user"].includes(role);
        });

        const profilesById = new Map();
        const profilesByName = new Map();

        recruiterProfiles.forEach((record) => {
          const idKey = cleanText(record?.user_id || record?.id);
          const nameKey = cleanText(record?.name).toLowerCase();
          if (idKey) profilesById.set(idKey, record);
          if (nameKey) profilesByName.set(nameKey, record);
        });

        const mergedRaw = [];
        const seenIds = new Set();
        const seenNames = new Set();

        const pushRecord = (record) => {
          const name = cleanText(record?.name || record?.full_name || record?.team_member);
          if (!name) return;

          const idKey = cleanText(record?.id || record?.user_id);
          const nameKey = name.toLowerCase();

          if (idKey && seenIds.has(idKey)) return;
          if (seenNames.has(nameKey)) return;

          if (idKey) seenIds.add(idKey);
          seenNames.add(nameKey);
          mergedRaw.push(record);
        };

        // The roster carries the database-backed card totals and activity
        // snapshots. The remaining endpoints only fill any missing members.
        rosterMembers.forEach(pushRecord);

        recruiterUsers.forEach((record) => {
          const nameKey = cleanText(record?.name).toLowerCase();
          const idKey = cleanText(record?.id);
          const profile = profilesById.get(idKey) || profilesByName.get(nameKey) || {};
          pushRecord({
            ...profile,
            ...record,
            id: record?.id ?? profile?.user_id ?? profile?.id,
            user_id: record?.id ?? profile?.user_id ?? profile?.id,
          });
        });

        recruiterProfiles.forEach((record) => {
          pushRecord({
            ...record,
            id: record?.user_id ?? record?.id,
            user_id: record?.user_id ?? record?.id,
          });
        });

        teamMembers.forEach((record) => {
          pushRecord({
            ...record,
            team_member: record?.name,
            role: "team member",
          });
        });

        const normalizedRows = mergedRaw
          .map((record, index) =>
            buildNormalizedTeamRow(record, { index })
          )
          .filter(Boolean)
          .sort((left, right) => left.name.localeCompare(right.name));

        setRows(normalizedRows);
      } catch (error) {
        console.error("Failed to load team page:", error);
        if (!active) return;
        setLoadError(error?.message || "Failed to load team members.");
        setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    loadTeamPage();

    return () => {
      active = false;
    };
  }, [user]);

  const ownerOptions = useMemo(() => {
    const admins = rows
      .filter((row) => ["admin", "administrator", "system_user"].includes(cleanText(row.role).toLowerCase()))
      .map((row) => row.name);

    const fallback = rows.slice(0, 4).map((row) => row.name);
    const values = Array.from(new Set([...admins, cleanText(user?.name), ...fallback].filter(Boolean)));
    return values.length ? values : ["Team Lead"];
  }, [rows, user]);

  const counts = useMemo(() => {
    const active = rows.filter((row) => row.status === "Active").length;
    const available = rows.filter((row) => row.status === "Available").length;
    const interviewing = rows.filter((row) => row.status === "Interviewing").length;
    const newcomers = rows.filter((row) => row.status === "New").length;
    const unavailable = rows.filter((row) => row.status === "Not Available").length;
    const assignments = rows.reduce((sum, row) => sum + Number(row.assignments || 0), 0);
    const assignedMembers = rows.filter((row) => Number(row.assignments || 0) > 0).length;

    return {
      active,
      available,
      interviewing,
      newcomers,
      unavailable,
      assignments,
      assignedMembers,
      total: rows.length,
    };
  }, [rows]);

  const topStats = useMemo(
    () => [
      { id: "members", value: formatNumber(counts.total), label: "Total Team Members" },
      { id: "active", value: formatNumber(counts.active), label: "Active Team Members" },
      { id: "assignments", value: formatNumber(counts.assignments), label: "Active Assignments" },
      { id: "interviewing", value: formatNumber(counts.interviewing), label: "Interviewing" },
      { id: "new", value: formatNumber(counts.newcomers), label: "New Sign Ups" },
      { id: "not-available", value: formatNumber(counts.unavailable), label: "Not Available" },
    ],
    [counts]
  );

  const filteredRows = useMemo(() => {
    const term = cleanText(search).toLowerCase();

    return rows.filter((row) => {
      const matchesTab =
        activeTab === "available"
          ? row.status === "Available"
          : true;

      if (!matchesTab) return false;
      if (teamFilters.role && cleanText(row.role) !== teamFilters.role) return false;
      if (teamFilters.status && row.status !== teamFilters.status) return false;
      if (teamFilters.location && row.location !== teamFilters.location) return false;
      if (activeStatId === "active" && row.status !== "Active") return false;
      if (activeStatId === "assignments" && Number(row.assignments || 0) <= 0) return false;
      if (activeStatId === "interviewing" && row.status !== "Interviewing") return false;
      if (activeStatId === "new" && row.status !== "New") return false;
      if (activeStatId === "not-available" && row.status !== "Not Available") return false;
      if (!term) return true;

      return [
        row.name,
        row.title,
        row.role,
        row.raw?.company_name,
        row.raw?.company,
        row.raw?.client_name,
        row.status,
        row.industryExpertise,
        row.location,
        row.engagementType,
        row.owner,
        row.email,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term));
    });
  }, [activeStatId, activeTab, rows, search, teamFilters]);

  const handleStatItemClick = useCallback((item) => {
    const nextId = String(item?.id || "members");
    setActiveStatId((current) => (current === nextId ? "members" : nextId));
  }, []);

  const teamFilterOptions = useMemo(() => ({
    roles: Array.from(new Set(rows.map((row) => cleanText(row.role)).filter(Boolean))).sort(),
    locations: Array.from(new Set(rows.map((row) => cleanText(row.location)).filter(Boolean))).sort(),
  }), [rows]);

  const selectedRows = useMemo(() => {
    const selected = new Set(selectedIds.map((value) => String(value)));
    return rows.filter((row) => selected.has(String(row.id)));
  }, [rows, selectedIds]);

  const handleApplyStatus = useCallback(
    (status) => {
      const nextStatus = cleanText(status);
      if (!nextStatus || !selectedIds.length) return;

      const selected = new Set(selectedIds.map((value) => String(value)));
      setRows((current) =>
        current.map((row) => (selected.has(String(row.id)) ? { ...row, status: nextStatus } : row))
      );
      setStatusFeedback({
        open: true,
        severity: "success",
        message: `Updated ${selectedIds.length} team member${selectedIds.length === 1 ? "" : "s"} to "${nextStatus}".`,
      });
    },
    [selectedIds]
  );

  const handleExport = useCallback(() => {
    const exportRows = selectedRows.length ? selectedRows : filteredRows;
    if (!exportRows.length) {
      showSnackbar("No team members available to export.", "info");
      return;
    }

    downloadCsv(exportRows);
    showSnackbar(
      `Exported ${exportRows.length} team member${exportRows.length === 1 ? "" : "s"} to CSV.`
    );
  }, [filteredRows, selectedRows, showSnackbar]);

  const handleDeleteSelected = useCallback(() => {
    if (!selectedIds.length) return;

    const selected = new Set(selectedIds.map((value) => String(value)));
    setRows((current) => current.filter((row) => !selected.has(String(row.id))));
    setSelectedIds([]);
    showSnackbar(
      `Removed ${selected.size} team member${selected.size === 1 ? "" : "s"} from the table.`
    );
  }, [selectedIds, showSnackbar]);

  const handleMatchToJob = useCallback(() => {
    const count = selectedRows.length;
    showSnackbar(
      count
        ? `Match to Job is ready for ${count} selected team member${count === 1 ? "" : "s"}.`
        : "Select team members first to match them to a job.",
      count ? "success" : "info"
    );
  }, [selectedRows, showSnackbar]);

  const handleMemberOverviewAction = useCallback((action, member) => {
    const actionLabel = action === "agent" ? "AI Agent" : action === "calendar" ? "Calendar" : "Assign";
    showSnackbar(`${actionLabel} is ready for ${member.name}.`, "info");
  }, [showSnackbar]);

  const handleOpenRowMenu = useCallback((event, row) => {
    setRowMenuAnchor(event.currentTarget);
    setRowMenuMember(row);
  }, []);

  const handleCloseRowMenu = useCallback(() => {
    setRowMenuAnchor(null);
    setRowMenuMember(null);
  }, []);

  const openUserView = useCallback(
    (member) => {
      const memberId = member?.id || member?.user_id;
      if (!memberId) return;

      navigate(`/team/${encodeURIComponent(String(memberId))}`, {
        state: { recruiter: member },
      });
    },
    [navigate]
  );

  const handleRowMenuAction = useCallback(
    (label) => {
      if (label === "View profile" && rowMenuMember) {
        const member = rowMenuMember;
        handleCloseRowMenu();
        openUserView(member);
        return;
      }
      const memberName = rowMenuMember?.name || "This team member";
      showSnackbar(`${label} is ready for ${memberName}.`, "info");
      handleCloseRowMenu();
    },
    [handleCloseRowMenu, openUserView, rowMenuMember, showSnackbar]
  );

  const handleShareMemberAction = useCallback(() => {
    const member = rowMenuMember;
    handleCloseRowMenu();
    if (!member) return;

    navigate(`/team/${encodeURIComponent(String(member.id || member.user_id))}`, {
      state: { recruiter: member, openShareProfile: true },
    });
  }, [handleCloseRowMenu, navigate, rowMenuMember]);

  const handleImportTeam = useCallback(
    async (importRows, setProgress) => {
      const importedRecords = (Array.isArray(importRows) ? importRows : [])
        .map(normalizeImportedRecord)
        .filter((record) => cleanText(record.name));

      if (!importedRecords.length) {
        throw new Error("No valid team members were found in that CSV.");
      }

      const existingNames = rows.map((row) => ({ id: row.id, name: row.name }));
      const normalized = [];
      let created = 0;
      let updated = 0;
      let skipped = importRows.length - importedRecords.length;

      for (let index = 0; index < importedRecords.length; index += 1) {
        const record = importedRecords[index];
        const exists = existingNames.some(
          (member) => cleanText(member.name).toLowerCase() === cleanText(record.name).toLowerCase()
        );
        const persisted = await createTeamMemberIfMissing({
          apiBase: API_BASE,
          name: record.name,
          existing: existingNames,
        });
        if (exists) {
          skipped += 1;
          setProgress(Math.round(((index + 1) / importedRecords.length) * 85) + 10);
          continue;
        }
        created += 1;
        existingNames.push(persisted || { name: record.name });
        const row = buildNormalizedTeamRow(
          { name: persisted?.name || record.name, id: persisted?.id || record.id },
          {
            index: rows.length + index,
            ownerPool: ownerOptions,
            currentUserName: cleanText(user?.name),
          }
        );
        if (row) normalized.push(row);
        setProgress(Math.round(((index + 1) / importedRecords.length) * 85) + 10);
      }

      setRows((current) => mergeNormalizedRows(current, normalized));
      showSnackbar(
        `${created} team member${created === 1 ? "" : "s"} added, ${skipped} skipped.`
      );
      return { created, updated, skipped };
    },
    [ownerOptions, rows, showSnackbar, user]
  );

  const handleOpenAddDialog = useCallback(() => {
    setAddDialogOpen(true);
  }, []);

  const handleCloseAddDialog = useCallback(() => {
    if (addSaving) return;
    setAddDialogOpen(false);
    setAddForm({
      name: "",
      email: "",
      title: "",
      location: "",
      status: "New",
      industry: "",
      engagementType: "RPO",
    });
  }, [addSaving]);

  const handleAddTeamMember = useCallback(async () => {
    const name = cleanText(addForm.name);
    if (!name) {
      showSnackbar("Team member name is required.", "error");
      return;
    }

    const duplicate = rows.some((row) => cleanText(row.name).toLowerCase() === name.toLowerCase());
    if (duplicate) {
      showSnackbar("That team member already exists in the list.", "warning");
      return;
    }

    setAddSaving(true);

    try {
      const created = await createTeamMemberIfMissing({
        apiBase: API_BASE,
        name,
        existing: rows.map((row) => ({ id: row.id, name: row.name })),
      });

      if (!created?.id) {
        throw new Error("The team member could not be saved to the database.");
      }

      const normalized = buildNormalizedTeamRow(
        {
          id: created.id,
          name,
          email: addForm.email,
          title: addForm.title,
          location: addForm.location,
          status: addForm.status,
          industry: addForm.industry,
          engagement_type: addForm.engagementType,
          role: "recruiter",
          owner: cleanText(user?.name),
        },
        {
          index: rows.length,
          ownerPool: ownerOptions,
          currentUserName: cleanText(user?.name),
        }
      );

      if (!normalized) throw new Error("Failed to build a team member row.");

      setRows((current) => mergeNormalizedRows(current, [normalized]));
      handleCloseAddDialog();
      showSnackbar(`Added ${name} to the Team page.`);
    } catch (error) {
      console.error("Failed to add team member:", error);
      showSnackbar(error?.message || "Failed to add team member.", "error");
    } finally {
      setAddSaving(false);
    }
  }, [addForm, handleCloseAddDialog, ownerOptions, rows, showSnackbar, user]);

  const topBarActions = useMemo(
    () => [
      {
        key: "import",
        label: "Import CSV",
        variant: "contained",
        startIcon: <Import size="16" color="currentColor" variant="Linear" />,
        onClick: () => setCsvImportOpen(true),
      },
      {
        key: "add",
        label: "Add Team Member",
        variant: "contained",
        startIcon: <TeamMemberAddIcon />,
        onClick: handleOpenAddDialog,
      },
    ],
    [handleOpenAddDialog, setCsvImportOpen]
  );

  const bulkActionsNode = useMemo(
    () => (
      <BulkActionsBar
        selectedCount={selectedRows.length}
        selectionLabel="team member"
        actions={[
          {
            type: "menu",
            label: "Change Status",
            options: STATUS_OPTIONS,
            onSelect: handleApplyStatus,
            feedback: statusFeedback,
            onFeedbackClose: () => setStatusFeedback((current) => ({ ...current, open: false })),
            disabled: selectedRows.length === 0,
            disabledMessage: "Please select at least one team member first.",
          },
          { type: "divider" },
          {
            type: "button",
            label: "Assign to Job",
            onClick: () =>
              showSnackbar(
                selectedRows.length
                  ? `Assign to Job is ready for ${selectedRows.length} selected team member${selectedRows.length === 1 ? "" : "s"}.`
                  : "Please select at least one team member first.",
                selectedRows.length ? "success" : "info"
              ),
            disabled: selectedRows.length === 0,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Assign to Client",
            onClick: () =>
              showSnackbar(
                selectedRows.length
                  ? `Assign to Client is ready for ${selectedRows.length} selected team member${selectedRows.length === 1 ? "" : "s"}.`
                  : "Please select at least one team member first.",
                selectedRows.length ? "success" : "info"
              ),
            disabled: selectedRows.length === 0,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Export",
            onClick: handleExport,
          },
          { type: "divider" },
          {
            type: "button",
            label: "Delete",
            onClick: handleDeleteSelected,
            disabled: selectedRows.length === 0,
          },
        ]}
        rightButton={{
          label: "Match to Job",
          confirm: true,
          background: PURPLE,
          startIcon: <MatchActionIcon size={18} />,
          onClick: handleMatchToJob,
        }}
      />
    ),
    [
      handleApplyStatus,
      handleDeleteSelected,
      handleExport,
      handleMatchToJob,
      selectedRows.length,
      showSnackbar,
      statusFeedback,
    ]
  );

  const columns = useMemo(
    () => [
      {
        id: "name",
        label: "Team Member",
        minWidth: 220,
        sortable: true,
        renderCell: (row) => <TeamMemberCell row={row} onOpen={openUserView} />,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 112,
        sortable: true,
        renderCell: (row) => <StatusChip value={row.status} />,
      },
      {
        id: "industryExpertise",
        label: "Industry Expertise",
        minWidth: 130,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: TEXT }}>
            {row.industryExpertise || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "location",
        label: "Location",
        minWidth: 100,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: TEXT }}>
            {row.location || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "engagementType",
        label: "Engagement Type",
        minWidth: 120,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: TEXT }}>
            {row.engagementType || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "assignments",
        label: "Assignments",
        minWidth: 105,
        sortable: true,
        renderCell: (row) => <AssignmentsCell row={row} />,
      },
      {
        id: "placements",
        label: "Placements",
        minWidth: 80,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: TEXT }}>
            {formatNumber(row.placements || 0)}
          </Typography>
        ),
      },
      {
        id: "lastActivity",
        label: "Last Activity",
        minWidth: 92,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: MUTED }}>
            {formatRelativeTimeShort(row.lastActivity, { emptyValue: EMPTY_VALUE })}
          </Typography>
        ),
      },
      {
        id: "owner",
        label: "Owner",
        minWidth: 118,
        sortable: true,
        renderCell: (row) => (
          <Typography sx={{ fontSize: 14, color: TEXT }}>
            {row.owner || EMPTY_VALUE}
          </Typography>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        minWidth: 86,
        sortable: false,
        renderCell: (row) => (
          <IconButton
            size="small"
            sx={{ color: "#98A2B3" }}
            onClick={(event) => handleOpenRowMenu(event, row)}
          >
            <MoreVertIcon sx={{ fontSize: 18, color: "currentColor" }} />
          </IconButton>
        ),
      },
    ],
    [handleOpenRowMenu, openUserView]
  );

  const renderRow = useCallback(
    (row) => (
      <>
        {columns.map((column) => (
          <TableCell
            key={column.id}
            sx={{
              minWidth: column.minWidth,
              py: 1.1,
              borderBottom: `1px solid ${BORDER}`,
              verticalAlign: "middle",
              ...(column.id === "actions" ? { textAlign: "right" } : {}),
            }}
          >
            {column.renderCell ? column.renderCell(row) : row[column.id]}
          </TableCell>
        ))}
      </>
    ),
    [columns]
  );

  return (
    <Box sx={{ py: 0, pb: 3, border: 0 }}>
      {!!loadError && (
        <Box sx={{ pb: 2 }}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      <ListTableTabsTemplate
        breadcrumb={
          <Breadcrumb
            items={[
              { label: "Overview", onClick: () => navigate("/overview") },
              { label: "Team", path: "/team" },
            ]}
          />
        }
        statsSection={
          <StatsSlider
            items={topStats}
            autoPlay={false}
            showArrows={true}
            cardMinWidth={200}
            gap={12}
            onItemClick={handleStatItemClick}
            activeItemId={activeStatId}
          />
        }
        contentSection={
          <Card
            sx={{
              p: 0,
              backgroundColor: "#fff",
              boxShadow: "none",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <TopListHeaderBar
              title="Team"
              searchPlaceholder="Search by name, title, expertise, company, location..."
              searchValue={search}
              onSearchChange={(event) => setSearch(event.target.value)}
              actions={topBarActions}
              resultsToolbarTargetId="team-results-toolbar"
            />

            <Box
              sx={{
                px: 2,
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <TabNavigationBar
                value={activeTab}
                onChange={(nextValue) => setActiveTab(nextValue)}
                items={TEAM_TABS.map((tab) => ({
                  value: tab.id,
                  label: tab.label,
                  count:
                    tab.id === "available"
                      ? formatNumber(counts.available)
                      : formatNumber(counts.total),
                }))}
                borderBottom={false}
              />
            </Box>

            <Box sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: `1px solid ${BORDER}` }}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.25 }}>
                <TextField select size="small" label="Role" value={teamFilters.role} onChange={(event) => setTeamFilters((current) => ({ ...current, role: event.target.value }))}>
                  <MenuItem value="">All Roles</MenuItem>
                  {teamFilterOptions.roles.map((role) => <MenuItem key={role} value={role}>{role}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Status" value={teamFilters.status} onChange={(event) => setTeamFilters((current) => ({ ...current, status: event.target.value }))}>
                  <MenuItem value="">All Statuses</MenuItem>
                  {STATUS_OPTIONS.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Location" value={teamFilters.location} onChange={(event) => setTeamFilters((current) => ({ ...current, location: event.target.value }))}>
                  <MenuItem value="">All Locations</MenuItem>
                  {teamFilterOptions.locations.map((location) => <MenuItem key={location} value={location}>{location}</MenuItem>)}
                </TextField>
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ p: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography sx={{ fontWeight: 700 }}>
                    Loading team members...
                  </Typography>
                </Stack>
              </Box>
            ) : activeTab === "overview" ? (
              <TeamOverviewDashboard
                members={filteredRows}
                counts={counts}
                onOpenMember={openUserView}
                onMemberAction={handleMemberOverviewAction}
              />
            ) : (
              <GenericDataTable
                columns={columns}
                exportFields={[...columns, ...TEAM_EXPORT_TAB_FIELDS].filter((field) => field.id !== "actions")}
                rows={filteredRows}
                renderRow={renderRow}
                includeSelectionColumn={true}
                selectedIds={selectedIds}
                onSelectedIdsChange={(ids) =>
                  setSelectedIds(Array.isArray(ids) ? ids.map((id) => String(id)) : [])
                }
                bulkActionsTop={bulkActionsNode}
                bulkActionsBottom={bulkActionsNode}
                defaultOrderBy="id"
                defaultOrderDirection="desc"
                initialRowsPerPage={10}
                visualVariant="reference"
                resultsToolbarTargetId="team-results-toolbar"
              />
            )}
          </Card>
        }
      />

      <CsvImportDialog
        open={csvImportOpen}
        onClose={() => setCsvImportOpen(false)}
        onImport={handleImportTeam}
        entityLabel="team members"
        instructions="Team import saves member names. Email, role, and profile details must be updated through the member profile. Existing names are kept."
      />

      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            minWidth: 180,
            borderRadius: 2.5,
            boxShadow: "0 16px 40px rgba(15,23,42,0.16)",
          },
        }}
      >
        <MenuItem onClick={() => handleRowMenuAction("View profile")} sx={{ fontSize: 13 }}>
          View profile
        </MenuItem>
        <MenuItem onClick={handleShareMemberAction} sx={{ fontSize: 13 }}>
          Share Profile
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction("Assign to job")} sx={{ fontSize: 13 }}>
          Assign to job
        </MenuItem>
        <MenuItem onClick={() => handleRowMenuAction("Email member")} sx={{ fontSize: 13 }}>
          Email member
        </MenuItem>
      </Menu>

      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle data-popup-header sx={{ fontWeight: 700 }}>Add Team Member</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Stack spacing={2}>
            <TextField
              label="Full name"
              value={addForm.name}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, name: event.target.value }))
              }
              fullWidth
              required
            />
            <TextField
              label="Email"
              value={addForm.email}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, email: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Title"
              value={addForm.title}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, title: event.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Location"
              value={addForm.location}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, location: event.target.value }))
              }
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Status"
                value={addForm.status}
                onChange={(event) =>
                  setAddForm((current) => ({ ...current, status: event.target.value }))
                }
                fullWidth
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Industry expertise"
                value={addForm.industry}
                onChange={(event) =>
                  setAddForm((current) => ({ ...current, industry: event.target.value }))
                }
                fullWidth
              />
            </Stack>
            <TextField
              select
              label="Engagement type"
              value={addForm.engagementType}
              onChange={(event) =>
                setAddForm((current) => ({ ...current, engagementType: event.target.value }))
              }
              fullWidth
            >
              {ENGAGEMENT_FALLBACKS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions data-popup-footer sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseAddDialog} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTeamMember}
            disabled={addSaving}
            sx={{
              borderRadius: 999,
              backgroundColor: PURPLE,
              textTransform: "none",
              px: 2.5,
              "&:hover": {
                backgroundColor: PURPLE,
              },
            }}
          >
            {addSaving ? "Adding..." : "Add Team Member"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3200}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

const Team = () => {
  const { user, loading } = useAuthController();

  if (loading) return <p>Loading...</p>;

  return <TeamContent user={user} />;
};

export default Team;
